import request from '../_helpers/request';

/**
 * Hot Search service for CMS management.
 */
export const hotSearchService = {
    GetHotSearchesByLangId,
    GetSearchBarHotSearches,
    CreateHotSearch,
    CreateHotSearchBatch,
    UpdateHotSearch,
    DeleteHotSearch,
    RestoreHotSearch,
    UpdateOrderHotSearches,
};

/**
 * Fetch hot searches for a given language.
 * API endpoint: HotSearch/GetListAsync
 * @param {number} langId - Language ID (1252=EN, 950=TC, 936=SC)
 * @param {boolean} [showOnlyActive] - If true, only return active records
 * @returns {Promise<{isSuccessful: boolean, data: Array}>}
 * Returns: [{ hotSearchId, langId, keyword, sortOrder, tagId, effectiveDateFrom, effectiveDateTo, createdDate, type }]
 */
function GetHotSearchesByLangId(langId, showOnlyActive = false) {
    return request({
        url: 'HotSearch/GetHotSearchesByLangId',
        method: 'get',
        params: { langId, showOnlyActive }
    });
}

/**
 * Fetch active hot searches for the search bar display.
 * API endpoint: HotSearch/GetSearchBarAsync
 * @param {number} langId - Language ID (1252=EN, 950=TC, 936=SC)
 * @returns {Promise<{isSuccessful: boolean, data: Array}>}
 * Returns: [{ keyword, sortOrder, type, tagId? }]
 */
function GetSearchBarHotSearches(langId) {
    return request({
        url: 'HotSearch/GetSearchBarHotSearches',
        method: 'get',
        params: { langId }
    });
}

/**
 * Create a new hot search entry.
 * API endpoint: HotSearch/CreateAsync
 * @param {object} data - { langId, tagId?, keyword, sortOrder, effectiveDateFrom?, effectiveDateTo?, type }
 * @returns {Promise<{isSuccessful: boolean, data: { hotSearchId: number }>}
 */
function CreateHotSearch(data) {
    return request({
        url: 'HotSearch/CreateAsync',
        method: 'post',
        data: data
    });
}

/**
 * Batch create hot search entries.
 * API endpoint: HotSearch/CreateBatchAsync
 * Tag-based entries expand to all 3 languages. Keyword items MUST include
 * their own `langId` — the backend rejects keyword items without it.
 *
 * @param {Array<{tagId?: number, keyword?: string, langId?: number, effectiveDateFrom?: string, effectiveDateTo?: string}>} data
 * @returns {Promise<{isSuccessful: boolean, data: { imported: number, skipped: number, errors?: Array<{index: number, message: string}> }}>}
 */
function CreateHotSearchBatch(data) {
    return request({
        url: 'HotSearch/CreateBatchAsync',
        method: 'post',
        data: data
    });
}

/**
 * Update an existing hot search entry.
 * API endpoint: HotSearch/UpdateAsync
 * @param {object} data - { id, langId, tagId?, keyword, sortOrder, effectiveDateFrom?, effectiveDateTo?, type }
 * @returns {Promise<{isSuccessful: boolean}>}
 */
function UpdateHotSearch(data) {
    return request({
        url: 'HotSearch/UpdateAsync',
        method: 'post',
        data: data
    });
}

/**
 * Delete a hot search entry.
 * API endpoint: HotSearch/DeleteAsync
 * @param {object} data - { entryId: number }
 * @returns {Promise<{isSuccessful: boolean}>}
 */
function DeleteHotSearch(data) {
    return request({
        url: 'HotSearch/DeleteAsync',
        method: 'post',
        data: data
    });
}

/**
 * Restore a soft-deleted hot search entry.
 * API endpoint: HotSearch/RestoreAsync
 * @param {object} data - { entryId: number }
 * @returns {Promise<{isSuccessful: boolean}>}
 */
function RestoreHotSearch(data) {
    return request({
        url: 'HotSearch/RestoreAsync',
        method: 'post',
        data: data
    });
}

/**
 * Batch reorder hot searches for a given language.
 * API endpoint: HotSearch/UpdateOrderAsync
 * @param {number} langId - Language ID (1252=EN, 950=TC, 936=SC)
 * @param {Array<{id: number, order: number}>} items - Array of id/order pairs
 * @returns {Promise<{isSuccessful: boolean}>}
 */
function UpdateOrderHotSearches(langId, items) {
    return request({
        url: 'HotSearch/UpdateOrderAsync',
        method: 'post',
        params: { langId },
        data: items
    });
}
